package top.wcpe.allincore.ui.runtime.material

import top.wcpe.allincore.ui.runtime.style.VisualModifier
import kotlin.math.ceil
import kotlin.math.max
import kotlin.math.min

private const val RECT_PART_COUNT = 4
private const val SINGLE_PART_COUNT = 1
private const val HEX_RADIX = 16
private const val MAX_TILE_PATCHES = 4096

/** FR-UI-021：资源纹理层采样模式。 */
enum class TextureLayerSampling(val id: String) {
    TILE("tile"),
    STRETCH("stretch"),
    NINE_SLICE("nineSlice"),
    COVER("cover"),
    ;

    companion object {
        fun parse(value: Any?): TextureLayerSampling = when (value?.toString()?.trim()?.lowercase()) {
            "tile" -> TILE
            "nineslice", "nine-slice", "nine_slice" -> NINE_SLICE
            "cover" -> COVER
            else -> STRETCH
        }
    }
}

/** FR-UI-021：纹理 UV 矩形，单位由平台纹理坐标解释。 */
data class TextureUv(val u: Float, val v: Float, val width: Float, val height: Float) {
    fun normalized(textureWidth: Float, textureHeight: Float): TextureUv = TextureUv(
        u.coerceIn(0f, textureWidth),
        v.coerceIn(0f, textureHeight),
        width.coerceIn(0f, textureWidth - u.coerceIn(0f, textureWidth)),
        height.coerceIn(0f, textureHeight - v.coerceIn(0f, textureHeight)),
    )

    fun hashPart(): String = "$u,$v,$width,$height"

    companion object {
        fun parse(value: Any?): TextureUv? = when (value) {
            null -> null
            is TextureUv -> parseParts(listOf(value.u, value.v, value.width, value.height))
            is Iterable<*> -> parseParts(value.toList())
            is Array<*> -> parseParts(value.toList())
            else -> parseParts(value.toString().split(',', ';'))
        }

        private fun parseParts(parts: List<Any?>): TextureUv? {
            if (parts.size != RECT_PART_COUNT) return null
            val values = parts.map { parseFiniteFloat(it) ?: return null }
            val (u, v, width, height) = values
            return TextureUv(u, v, max(0f, width), max(0f, height))
        }
    }
}

/** FR-UI-021：nineSlice 四边固定尺寸。 */
data class TextureNineSlice(val left: Float, val top: Float, val right: Float, val bottom: Float) {
    fun hashPart(): String = "$left,$top,$right,$bottom"

    companion object {
        val ZERO = TextureNineSlice(0f, 0f, 0f, 0f)

        fun parse(value: Any?): TextureNineSlice = when (value) {
            null -> ZERO
            is TextureNineSlice -> normalize(value)
            is Number -> fromSingle(safeDimension(value))
            is Iterable<*> -> parseParts(value.toList())
            is Array<*> -> parseParts(value.toList())
            else -> parseParts(value.toString().split(',', ';'))
        }

        private fun fromSingle(value: Float): TextureNineSlice = TextureNineSlice(value, value, value, value)

        private fun parseParts(parts: List<Any?>): TextureNineSlice {
            if (parts.size == SINGLE_PART_COUNT) return fromSingle(safeDimension(parts.first()))
            if (parts.size != RECT_PART_COUNT) return ZERO
            val (left, top, right, bottom) = parts.map(::safeDimension)
            return TextureNineSlice(left, top, right, bottom)
        }

        private fun normalize(value: TextureNineSlice): TextureNineSlice = TextureNineSlice(
            safeDimension(value.left),
            safeDimension(value.top),
            safeDimension(value.right),
            safeDimension(value.bottom),
        )

        private fun safeDimension(value: Any?): Float = parseFiniteFloat(value)?.coerceAtLeast(0f) ?: 0f
    }
}

enum class TextureLayerFallbackReason {
    NONE,
    MISSING_SOURCE,
    INVALID_SOURCE,
    MISSING_RESOURCE,
}

/** FR-UI-021：平台中立 textureLayer recipe，动态字段不参与静态 hash。 */
data class TextureLayerRecipe(
    val source: String,
    val sampling: TextureLayerSampling,
    val uv: TextureUv?,
    val scale: Float,
    val tint: Int,
    val opacity: Float,
    val blend: String,
    val nineSlice: TextureNineSlice,
) {
    val recipeHash: String = hashStaticFields()

    fun fallbackReason(resourceExists: (String) -> Boolean): TextureLayerFallbackReason = when {
        source.isBlank() -> TextureLayerFallbackReason.MISSING_SOURCE
        !isValidResourceLocation(source) -> TextureLayerFallbackReason.INVALID_SOURCE
        !resourceExists(source) -> TextureLayerFallbackReason.MISSING_RESOURCE
        else -> TextureLayerFallbackReason.NONE
    }

    fun hasFallback(resourceExists: (String) -> Boolean): Boolean =
        fallbackReason(resourceExists) != TextureLayerFallbackReason.NONE

    /** 输出给平台桥的稳定规范字段，避免平台重复解释原始 DSL 值。 */
    fun toBridgeFields(): Map<String, Any?> = mapOf(
        "source" to source,
        "sampling" to sampling.id,
        "uv" to uv?.hashPart(),
        "scale" to scale,
        "tint" to tint,
        "opacity" to opacity,
        "blend" to blend,
        "nineSlice" to nineSlice.hashPart(),
    )

    private fun hashStaticFields(): String = sha256(
        listOf(
            source,
            sampling.id,
            uv?.hashPart().orEmpty(),
            scale.toString(),
            tint.toUInt().toString(HEX_RADIX),
            nineSlice.hashPart(),
        ).joinToString("|"),
    )

    companion object {
        /** VisualModifier 是唯一 typed 入口；所有字段统一复用 fromFields 规范化。 */
        fun fromModifier(modifier: VisualModifier.TextureLayer): TextureLayerRecipe = fromFields(
            mapOf(
                "source" to modifier.source,
                "sampling" to modifier.sampling,
                "uv" to modifier.uv,
                "scale" to modifier.scale,
                "tint" to modifier.tint,
                "opacity" to modifier.opacity,
                "blend" to modifier.blend,
                "nineSlice" to modifier.nineSlice,
            ),
        )

        fun fromFields(fields: Map<String, Any?>): TextureLayerRecipe = TextureLayerRecipe(
            source = normalizeSource(fields["source"]),
            sampling = TextureLayerSampling.parse(fields["sampling"]),
            uv = TextureUv.parse(fields["uv"]),
            scale = normalizePositiveFloat(fields["scale"], 1f),
            tint = parseTint(fields["tint"]),
            opacity = normalizeOpacity(fields["opacity"]),
            blend = normalizeBlend(fields["blend"]),
            nineSlice = TextureNineSlice.parse(fields["nineSlice"] ?: fields["nine_slice"]),
        )
    }
}

/** FR-UI-021：静态材质缓存 key。 */
data class TextureLayerStaticCacheKey(
    val reloadVersion: Long,
    val widthBucket: Int,
    val heightBucket: Int,
    val recipeHash: String,
) {
    companion object {
        private const val BUCKET_SIZE = 8

        fun of(
            reloadVersion: Long,
            width: Float,
            height: Float,
            recipe: TextureLayerRecipe,
        ): TextureLayerStaticCacheKey = TextureLayerStaticCacheKey(
            reloadVersion,
            bucket(width),
            bucket(height),
            recipe.recipeHash,
        )

        private fun bucket(value: Float): Int {
            if (!value.isFinite() || value <= 0f) return 0
            val bucketCount = ceil(value / BUCKET_SIZE).toLong()
            if (bucketCount >= Int.MAX_VALUE / BUCKET_SIZE) return Int.MAX_VALUE
            return bucketCount.toInt() * BUCKET_SIZE
        }
    }
}

/** FR-UI-021：小型静态缓存容器，key 自带 reload version，reload 后自然失效。 */
class TextureLayerStaticCache<T : Any> {
    private val entries = linkedMapOf<TextureLayerStaticCacheKey, T>()

    fun getOrPut(key: TextureLayerStaticCacheKey, factory: () -> T): T = entries.getOrPut(key, factory)

    fun size(): Int = entries.size

    fun clear() {
        entries.clear()
    }
}

/** FR-UI-021：平台渲染可消费的采样 patch。 */
data class TextureLayerPatch(val dst: TextureLayerRect, val uv: TextureUv)

data class TextureLayerRect(val x: Float, val y: Float, val width: Float, val height: Float)

private data class TileLayout(
    val width: Float,
    val height: Float,
    val tileWidth: Float,
    val tileHeight: Float,
    val uv: TextureUv,
)

private data class NineSliceCuts(
    val x: List<Pair<Float, Float>>,
    val y: List<Pair<Float, Float>>,
    val u: List<Pair<Float, Float>>,
    val v: List<Pair<Float, Float>>,
)

/** FR-UI-021：把 recipe 归一化成 tile/stretch/nineSlice/cover 采样计划。 */
data class TextureLayerRenderPlan(val patches: List<TextureLayerPatch>) {
    companion object {
        fun build(
            recipe: TextureLayerRecipe,
            width: Float,
            height: Float,
            textureWidth: Float,
            textureHeight: Float,
        ): TextureLayerRenderPlan {
            if (!validDimensions(width, height, textureWidth, textureHeight)) return TextureLayerRenderPlan(emptyList())
            val uv = (
                recipe.uv ?: TextureUv(
                    0f,
                    0f,
                    textureWidth,
                    textureHeight,
                )
                ).normalized(textureWidth, textureHeight)
            if (!validUv(uv)) return TextureLayerRenderPlan(emptyList())
            val patches = when (recipe.sampling) {
                TextureLayerSampling.TILE -> tile(width, height, uv, normalizePositiveFloat(recipe.scale, 1f))
                TextureLayerSampling.STRETCH -> listOf(patch(0f, 0f, width, height, uv))
                TextureLayerSampling.NINE_SLICE -> nineSlice(
                    width,
                    height,
                    uv,
                    TextureNineSlice.parse(recipe.nineSlice),
                )
                TextureLayerSampling.COVER -> listOf(patch(0f, 0f, width, height, coverUv(width, height, uv)))
            }
            return TextureLayerRenderPlan(patches.filter(::validPatch))
        }

        private fun validDimensions(width: Float, height: Float, textureWidth: Float, textureHeight: Float): Boolean =
            width.isFinite() &&
                height.isFinite() &&
                textureWidth.isFinite() &&
                textureHeight.isFinite() &&
                width > 0f &&
                height > 0f &&
                textureWidth > 0f &&
                textureHeight > 0f

        private fun validUv(uv: TextureUv): Boolean = uv.u.isFinite() &&
            uv.v.isFinite() &&
            uv.width.isFinite() &&
            uv.height.isFinite() &&
            uv.width > 0f &&
            uv.height > 0f

        private fun validPatch(value: TextureLayerPatch): Boolean = value.dst.x.isFinite() &&
            value.dst.y.isFinite() &&
            value.dst.width.isFinite() &&
            value.dst.height.isFinite() &&
            value.dst.width > 0f &&
            value.dst.height > 0f &&
            validUv(value.uv)

        private fun tile(width: Float, height: Float, uv: TextureUv, scale: Float): List<TextureLayerPatch> {
            val tileWidth = max(1f, uv.width * scale)
            val tileHeight = max(1f, uv.height * scale)
            val columns = ceil(width / tileWidth).toLong()
            val rows = ceil(height / tileHeight).toLong()
            if (columns <= 0L ||
                rows <= 0L ||
                columns > MAX_TILE_PATCHES ||
                rows > MAX_TILE_PATCHES ||
                columns * rows > MAX_TILE_PATCHES
            ) {
                return emptyList()
            }
            val patches = ArrayList<TextureLayerPatch>((columns * rows).toInt())
            val layout = TileLayout(width, height, tileWidth, tileHeight, uv)
            var y = 0f
            while (y < height) {
                addTileRow(patches, layout, y)
                y += tileHeight
            }
            return patches
        }

        private fun addTileRow(patches: MutableList<TextureLayerPatch>, layout: TileLayout, y: Float) {
            var x = 0f
            val drawHeight = min(layout.tileHeight, layout.height - y)
            while (x < layout.width) {
                val drawWidth = min(layout.tileWidth, layout.width - x)
                patches += patch(
                    x,
                    y,
                    drawWidth,
                    drawHeight,
                    clippedUv(
                        layout.uv,
                        drawWidth / layout.tileWidth,
                        drawHeight / layout.tileHeight,
                    ),
                )
                x += layout.tileWidth
            }
        }

        private fun nineSlice(
            width: Float,
            height: Float,
            uv: TextureUv,
            slice: TextureNineSlice,
        ): List<TextureLayerPatch> {
            val cuts = NineSliceCuts(
                x = cuts(width, slice.left, slice.right),
                y = cuts(height, slice.top, slice.bottom),
                u = cuts(uv.width, slice.left, slice.right, uv.u),
                v = cuts(uv.height, slice.top, slice.bottom, uv.v),
            )
            return buildList {
                for (row in 0..2) for (col in 0..2) add(slicePatch(cuts, row, col))
            }
        }

        private fun slicePatch(cuts: NineSliceCuts, row: Int, col: Int): TextureLayerPatch = patch(
            cuts.x[col].first,
            cuts.y[row].first,
            cuts.x[col].second,
            cuts.y[row].second,
            TextureUv(cuts.u[col].first, cuts.v[row].first, cuts.u[col].second, cuts.v[row].second),
        )

        private fun coverUv(width: Float, height: Float, uv: TextureUv): TextureUv {
            if (hasInvalidArea(width, height, uv)) return uv
            val targetAspect = width / height
            val sourceAspect = uv.width / uv.height
            return if (sourceAspect >
                targetAspect
            ) {
                cropWidth(uv, uv.height * targetAspect)
            } else {
                cropHeight(uv, uv.width / targetAspect)
            }
        }

        private fun hasInvalidArea(width: Float, height: Float, uv: TextureUv): Boolean =
            width <= 0f || height <= 0f || uv.width <= 0f || uv.height <= 0f

        private fun cropWidth(uv: TextureUv, newWidth: Float): TextureUv =
            TextureUv(uv.u + (uv.width - newWidth) / 2f, uv.v, newWidth, uv.height)

        private fun cropHeight(uv: TextureUv, newHeight: Float): TextureUv =
            TextureUv(uv.u, uv.v + (uv.height - newHeight) / 2f, uv.width, newHeight)

        private fun clippedUv(uv: TextureUv, widthRatio: Float, heightRatio: Float): TextureUv =
            TextureUv(uv.u, uv.v, uv.width * widthRatio.coerceIn(0f, 1f), uv.height * heightRatio.coerceIn(0f, 1f))

        private fun patch(x: Float, y: Float, width: Float, height: Float, uv: TextureUv): TextureLayerPatch =
            TextureLayerPatch(TextureLayerRect(x, y, width, height), uv)

        private fun cuts(total: Float, start: Float, end: Float, offset: Float = 0f): List<Pair<Float, Float>> {
            val safeStart = start.coerceIn(0f, total / 2f)
            val safeEnd = end.coerceIn(0f, total - safeStart)
            val middle = max(0f, total - safeStart - safeEnd)
            return listOf(offset to safeStart, offset + safeStart to middle, offset + total - safeEnd to safeEnd)
        }
    }
}

private fun normalizeSource(value: Any?): String = value?.toString()?.trim()?.lowercase().orEmpty()

private fun normalizePositiveFloat(value: Any?, defaultValue: Float): Float {
    val parsed = parseFloat(value) ?: return defaultValue
    return if (parsed.isFinite() && parsed > 0f) parsed else defaultValue
}

private fun normalizeOpacity(value: Any?): Float = parseFiniteFloat(value)?.coerceIn(0f, 1f) ?: 1f

private fun normalizeBlend(value: Any?): String = when (value?.toString()?.trim()?.lowercase()) {
    "multiply" -> "multiply"
    "screen" -> "screen"
    "add", "additive" -> "additive"
    else -> "normal"
}

private fun parseTint(value: Any?): Int = when (value) {
    null -> 0xFFFFFFFF.toInt()
    is Number -> value.toInt()
    else -> parseTintString(value.toString())
}

private fun parseTintString(value: String): Int {
    val normalized = value.trim().removePrefix("#").removePrefix("0x").removePrefix("0X")
    return normalized.toLongOrNull(16)?.toInt() ?: 0xFFFFFFFF.toInt()
}

private fun parseFloat(value: Any?): Float? = when (value) {
    null -> null
    is Number -> value.toFloat()
    else -> value.toString().trim().toFloatOrNull()
}

private fun parseFiniteFloat(value: Any?): Float? = parseFloat(value)?.takeIf(Float::isFinite)

private fun isValidResourceLocation(value: String): Boolean = Regex("^[a-z0-9_.-]+:[a-z0-9_./-]+$").matches(value)

/**
 * KMP 稳定 64 位 FNV-1a 十六进制摘要，替代 java MessageDigest(SHA-256)。
 * 仅用于 recipe 静态字段缓存键：同输入同输出、不同静态字段必不同；不要求跨算法兼容旧 SHA 值。
 */
private fun sha256(value: String): String {
    var hash = -0x340d631b7bdddcdbL // FNV offset basis 64
    val prime = 0x100000001b3L
    for (ch in value) {
        hash = hash xor ch.code.toLong()
        hash *= prime
    }
    // 固定 16 位小写十六进制（无 Locale 依赖）
    val hex = "0123456789abcdef"
    val chars = CharArray(16)
    var h = hash
    for (i in 15 downTo 0) {
        chars[i] = hex[(h and 0xfL).toInt()]
        h = h ushr 4
    }
    return chars.concatToString()
}
